import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import DefinitionSearch from "./DefinitionSearch"
import DefinitionMenu from "./DefinitionMenu"
import TrashMenu from "./TrashMenu"
import {
  useCreateKnowledgeBaseMutation,
  useGetKnowledgeBasesQuery,
} from "@/rag"
import type { ActionCreatorResult } from "@/base"
import type { Uuid } from "@/utils"
import { useDebounce } from "@/utils"
import { useCurrentTenant } from "@/tenant"
import {
  useCreateEvaluationMutation,
  useGetEvaluationsQuery,
} from "@/evaluation"
import { useCreatePromptMutation, useGetPromptsQuery } from "@/prompting"
import { useCreateWorkflowMutation, useGetWorkflowsQuery } from "@/workflow"

export default function DefinitionMenus() {
  const { tenantSlug } = useCurrentTenant()
  const { data: prompts } = useGetPromptsQuery()
  const [createPrompt] = useCreatePromptMutation()
  const { data: workflows } = useGetWorkflowsQuery()
  const [createWorkflow] = useCreateWorkflowMutation()
  const { data: evaluations } = useGetEvaluationsQuery()
  const [createEvaluation] = useCreateEvaluationMutation()
  const { data: knowledgeBases } = useGetKnowledgeBasesQuery()
  const [createKnowledgeBase] = useCreateKnowledgeBaseMutation()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebounce(searchQuery, { delay: "short" })

  const handleCreate =
    (mutation: () => ActionCreatorResult<{ id: Uuid }>, route: string) =>
    async () => {
      const { id } = await mutation().unwrap()
      await navigate({
        to: route,
        params: { tenantSlug, id },
      })
    }

  return (
    <>
      <DefinitionSearch onChange={setSearchQuery} />
      <DefinitionMenu
        type="prompt"
        definitions={prompts ?? []}
        onCreateClick={handleCreate(createPrompt, "/t/$tenantSlug/prompts/$id")}
        searchQuery={debouncedSearchQuery}
      />
      <DefinitionMenu
        type="workflow"
        definitions={workflows ?? []}
        onCreateClick={handleCreate(
          createWorkflow,
          "/t/$tenantSlug/workflows/$id",
        )}
        searchQuery={debouncedSearchQuery}
      />
      <DefinitionMenu
        type="evaluation"
        definitions={evaluations ?? []}
        onCreateClick={handleCreate(
          createEvaluation,
          "/t/$tenantSlug/evaluations/$id",
        )}
        searchQuery={debouncedSearchQuery}
      />
      <DefinitionMenu
        type="knowledge-base"
        definitions={knowledgeBases ?? []}
        onCreateClick={handleCreate(
          createKnowledgeBase,
          "/t/$tenantSlug/knowledge-bases/$id",
        )}
        searchQuery={debouncedSearchQuery}
      />
      <TrashMenu searchQuery={debouncedSearchQuery} />
    </>
  )
}
